import {
  CircularProgress,
  LinearProgress,
  PrimaryButton,
  Table,
  TableContainer,
  Typography,
} from '@bb-ui/react-library';
import * as React from 'react';
import { TableHead } from '@bb-ui/react-library/dist/components/TableHead';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import { useTranslation } from 'react-i18next';
import { TableBody } from '@bb-ui/react-library/dist/components/TableBody';
import { JobExecutionRow } from './JobExecutionRow';
import { LoadingIndicator } from '../LoadingIndicator';
import { useStyles } from '../JobList';
import { JobExecutionContext } from '../../contexts/JobExecutionContext';
import { useJobRegion } from 'contexts/JobRegionContext';
import { JobRegionPicker } from 'pages/Tenant/JobRegionPicker/JobRegionPicker';
import { useExecutionSearch } from 'hooks/useExecutionSearch';
import { ExecutionSearchField } from './ExecutionSearchField';

export const JobExecutionTable: React.FC = (props) => {
  const { t } = useTranslation();
  const classes = useStyles(props);
  const c = React.useContext(JobExecutionContext);
  const { showRegionPicker, isJobRegionReady } = useJobRegion();
  const search = useExecutionSearch({
    executions: c.executions,
    nextPageToken: c.nextPageToken ?? c.siteNextPageToken,
    loading: c.loading,
    loadMore: c.loadMore,
  });

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (
        c.executions.some((execution) =>
          [
            'IN_PROGRESS',
            'PENDING',
            'QUEUED',
            'CANCELLATION_REQUESTED',
            'EXECUTION_TIMEOUT_PENDING',
          ].includes(execution.status),
        )
      ) {
        c.refresh();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [c.executions, c]);

  let tableBody: React.ReactNode;
  if (c.loading && c.executions.length === 0) {
    tableBody = (
      <TableRow>
        <TableCell colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
          <CircularProgress ariaLabel="Loading Jobs" />
        </TableCell>
      </TableRow>
    );
  } else if (search.searchPending) {
    tableBody = (
      <TableRow>
        <TableCell colSpan={5} data-testid="jobs-list-search-pending">
          {t('jobService.executionSearching')}
        </TableCell>
      </TableRow>
    );
  } else if (search.isSearching && search.matches.length === 0) {
    tableBody = (
      <TableRow>
        <TableCell colSpan={5} data-testid="jobs-list-no-search-match">
          {t('jobService.executionNoMatch', { searchExpression: search.searchTerm })}
        </TableCell>
      </TableRow>
    );
  } else {
    tableBody = search.matches.map((execution) => (
      <JobExecutionRow key={execution.executionId} jobExecution={execution} />
    ));
  }

  return (
    <div>
      <ExecutionSearchField
        id="job-executions-search"
        value={search.inputValue}
        onChange={search.onInputChange}
      >
        {showRegionPicker && isJobRegionReady && <JobRegionPicker />}
      </ExecutionSearchField>
      {search.searchCapped && (
        <Typography data-testid="jobs-list-search-notice">
          {t('jobService.executionSearchPartial')}
        </Typography>
      )}
      <TableContainer>
        {c.loading && c.executions.length > 0 && <LinearProgress ariaLabel="Loading Jobs" />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{tableBody}</TableBody>
        </Table>

        {!c.loading &&
          (c.nextPageToken || c.siteNextPageToken ? (
            <div className={classes.loadMoreButton}>
              {c.loading ? (
                <LoadingIndicator data-testid="load-more-loading" />
              ) : (
                <PrimaryButton onClick={() => search.loadMorePage()}>
                  {t('global.loadMore')}
                </PrimaryButton>
              )}
            </div>
          ) : (
            <Typography className={classes.loadMoreButton} data-testid="jobs-list-no-more-data">
              {t('jobService.noMoreData')}
            </Typography>
          ))}
      </TableContainer>
    </div>
  );
};
